import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _inheritsLoose from "@babel/runtime/helpers/esm/inheritsLoose";
import invariant from 'invariant';
import React from 'react';
import raf from 'raf';
import { isIE11, isEdge } from "../../../lib/client";
import { isKeyBackspace, isKeyDelete, someKeys } from "../../../lib/events/keyboard/identifiers";
import { polyfillPlaceholder } from "../../../lib/polyfillPlaceholder";
import { MaskedInput } from "../../../internal/MaskedInput";
import { ThemeContext } from "../../../lib/theming/ThemeContext";
import { CommonWrapper } from "../../../internal/CommonWrapper";
import { cx } from "../../../lib/theming/Emotion";
import { styles } from "../Input.styles";
/**
 * Интерфес пропсов наследуется от `React.InputHTMLAttributes<HTMLInputElement>`.
 *  Все пропсы кроме перечисленных, `className` и `style` передаются в `<input>`
 */

export var Input = /*#__PURE__*/function (_React$Component) {
  _inheritsLoose(Input, _React$Component);

  function Input() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _React$Component.call.apply(_React$Component, [this].concat(args)) || this;
    _this.state = {
      polyfillPlaceholder: false,
      blinking: false,
      focused: false
    };
    _this.selectAllId = null;
    _this.theme = void 0;
    _this.blinkTimeout = 0;
    _this.input = null;

    _this.selectAll = function () {
      if (_this.input) {
        _this.setSelectionRange(0, _this.input.value.length);
      }
    };

    _this.delaySelectAll = function () {
      return _this.selectAllId = raf(_this.selectAll);
    };

    _this.cancelDelayedSelectAll = function () {
      if (_this.selectAllId) {
        raf.cancel(_this.selectAllId);
        _this.selectAllId = null;
      }
    };

    _this.cancelBlink = function (callback) {
      if (_this.blinkTimeout) {
        clearTimeout(_this.blinkTimeout);
        _this.blinkTimeout = 0;

        if (_this.state.blinking) {
          _this.setState({
            blinking: false
          }, callback);

          return;
        }
      }

      if (callback) {
        callback();
      }
    };

    _this.renderMain = function (props) {
      var _cx, _cx2;

      var onMouseEnter = props.onMouseEnter,
          onMouseLeave = props.onMouseLeave,
          onMouseOver = props.onMouseOver,
          onKeyDown = props.onKeyDown,
          onKeyPress = props.onKeyPress,
          onValueChange = props.onValueChange,
          width = props.width,
          error = props.error,
          warning = props.warning,
          leftIcon = props.leftIcon,
          rightIcon = props.rightIcon,
          borderless = props.borderless,
          value = props.value,
          align = props.align,
          type = props.type,
          mask = props.mask,
          maskChar = props.maskChar,
          alwaysShowMask = props.alwaysShowMask,
          size = props.size,
          placeholder = props.placeholder,
          selectAllOnFocus = props.selectAllOnFocus,
          disabled = props.disabled,
          onUnexpectedInput = props.onUnexpectedInput,
          prefix = props.prefix,
          suffix = props.suffix,
          formatChars = props.formatChars,
          rest = _objectWithoutPropertiesLoose(props, ["onMouseEnter", "onMouseLeave", "onMouseOver", "onKeyDown", "onKeyPress", "onValueChange", "width", "error", "warning", "leftIcon", "rightIcon", "borderless", "value", "align", "type", "mask", "maskChar", "alwaysShowMask", "size", "placeholder", "selectAllOnFocus", "disabled", "onUnexpectedInput", "prefix", "suffix", "formatChars"]);

      var _this$state = _this.state,
          blinking = _this$state.blinking,
          focused = _this$state.focused;
      var labelProps = {
        className: cx(styles.root(_this.theme), _this.getSizeClassName(), (_cx = {}, _cx[styles.focus(_this.theme)] = focused, _cx[styles.blink(_this.theme)] = blinking, _cx[styles.borderless()] = borderless && !focused, _cx[styles.disabled(_this.theme)] = disabled, _cx[styles.warning(_this.theme)] = warning, _cx[styles.error(_this.theme)] = error, _cx[styles.focusFallback(_this.theme)] = focused && (isIE11 || isEdge), _cx[styles.warningFallback(_this.theme)] = warning && (isIE11 || isEdge), _cx[styles.errorFallback(_this.theme)] = error && (isIE11 || isEdge), _cx)),
        style: {
          width: width
        },
        onMouseEnter: onMouseEnter,
        onMouseLeave: onMouseLeave,
        onMouseOver: onMouseOver
      };

      var inputProps = _extends({}, rest, {
        className: cx(styles.input(_this.theme), (_cx2 = {}, _cx2[styles.inputFocus(_this.theme)] = focused, _cx2[styles.inputDisabled(_this.theme)] = disabled, _cx2)),
        value: value,
        onChange: _this.handleChange,
        onFocus: _this.handleFocus,
        onKeyDown: _this.handleKeyDown,
        onKeyPress: _this.handleKeyPress,
        onBlur: _this.handleBlur,
        style: {
          textAlign: align
        },
        ref: _this.refInput,
        type: 'text',
        placeholder: !_this.isMaskVisible && !polyfillPlaceholder ? placeholder : undefined,
        disabled: disabled
      });

      if (type === 'password') {
        inputProps.type = type;
      }

      var input = mask ? _this.renderMaskedInput(inputProps, mask) : /*#__PURE__*/React.createElement('input', inputProps);
      return /*#__PURE__*/React.createElement("label", labelProps, /*#__PURE__*/React.createElement("span", {
        className: styles.sideContainer()
      }, _this.renderLeftIcon(), _this.renderPrefix()), /*#__PURE__*/React.createElement("span", {
        className: styles.wrapper()
      }, input, _this.renderPlaceholder()), /*#__PURE__*/React.createElement("span", {
        className: cx(styles.sideContainer(), styles.rightContainer())
      }, _this.renderSuffix(), _this.renderRightIcon()));
    };

    _this.refInput = function (element) {
      if (element instanceof MaskedInput) {
        _this.input = element.input;
      } else {
        _this.input = element;
      }
    };

    _this.handleChange = function (event) {
      if (polyfillPlaceholder) {
        var fieldIsEmpty = event.target.value === '';

        if (_this.state.polyfillPlaceholder !== fieldIsEmpty) {
          _this.setState({
            polyfillPlaceholder: fieldIsEmpty
          });
        }
      }

      if (_this.props.onValueChange) {
        _this.props.onValueChange(event.target.value);
      }

      if (_this.props.onChange) {
        _this.props.onChange(event);
      }
    };

    _this.handleFocus = function (event) {
      _this.setState({
        focused: true
      });

      if (_this.props.selectAllOnFocus) {
        // https://github.com/facebook/react/issues/7769
        _this.input && !isIE11 ? _this.selectAll() : _this.delaySelectAll();
      }

      if (_this.props.onFocus) {
        _this.props.onFocus(event);
      }
    };

    _this.handleKeyDown = function (e) {
      if (_this.props.onKeyDown) {
        _this.props.onKeyDown(e);
      }

      var isDeleteKey = someKeys(isKeyBackspace, isKeyDelete)(e);

      if (!e.currentTarget.value && isDeleteKey && !e.repeat) {
        _this.handleUnexpectedInput();
      }
    };

    _this.handleKeyPress = function (event) {
      if (_this.props.onKeyPress) {
        _this.props.onKeyPress(event);
      }

      if (_this.props.maxLength === event.currentTarget.value.length) {
        _this.handleUnexpectedInput(event.currentTarget.value);
      }
    };

    _this.handleMaskedValueChange = function (value) {
      if (_this.props.onValueChange) {
        _this.props.onValueChange(value);
      }
    };

    _this.handleUnexpectedInput = function (value) {
      if (value === void 0) {
        value = _this.props.value || '';
      }

      if (_this.props.onUnexpectedInput) {
        _this.props.onUnexpectedInput(value);
      } else {
        _this.blink();
      }
    };

    _this.handleBlur = function (event) {
      _this.setState({
        focused: false
      });

      if (_this.props.onBlur) {
        _this.props.onBlur(event);
      }
    };

    _this.renderPrefix = function () {
      var _cx3;

      var _this$props = _this.props,
          prefix = _this$props.prefix,
          disabled = _this$props.disabled;

      if (!prefix) {
        return null;
      }

      return /*#__PURE__*/React.createElement("span", {
        className: cx(styles.prefix(_this.theme), (_cx3 = {}, _cx3[styles.prefixDisabled(_this.theme)] = disabled, _cx3))
      }, prefix);
    };

    _this.renderSuffix = function () {
      var _cx4;

      var _this$props2 = _this.props,
          suffix = _this$props2.suffix,
          disabled = _this$props2.disabled;

      if (!suffix) {
        return null;
      }

      return /*#__PURE__*/React.createElement("span", {
        className: cx(styles.suffix(_this.theme), (_cx4 = {}, _cx4[styles.suffixDisabled(_this.theme)] = disabled, _cx4))
      }, suffix);
    };

    return _this;
  }

  var _proto = Input.prototype;

  _proto.componentDidMount = function componentDidMount() {
    if (polyfillPlaceholder) {
      this.setState({
        polyfillPlaceholder: true
      });
    }
  };

  _proto.componentWillUnmount = function componentWillUnmount() {
    if (this.blinkTimeout) {
      clearTimeout(this.blinkTimeout);
    }

    this.cancelDelayedSelectAll();
  };

  _proto.UNSAFE_componentWillReceiveProps = function UNSAFE_componentWillReceiveProps(nextProps) {
    if (polyfillPlaceholder && !nextProps.value) {
      this.setState({
        polyfillPlaceholder: true
      });
    }
  }
  /**
  * @public
  */
  ;

  _proto.focus = function focus() {
    invariant(this.input, 'Cannot call "focus" because Input is not mounted');
    this.input.focus();
  }
  /**
  * @public
  */
  ;

  _proto.blur = function blur() {
    invariant(this.input, 'Cannot call "blur" because Input is not mounted');
    this.input.blur();
  }
  /**
  * @public
  */
  ;

  _proto.blink = function blink() {
    var _this2 = this;

    if (this.blinkTimeout) {
      this.cancelBlink(function () {
        // trigger reflow to restart animation
        // @see https://css-tricks.com/restart-css-animation/#article-header-id-0
        void (_this2.input && _this2.input.offsetWidth);

        _this2.blink();
      });
      return;
    }

    this.setState({
      blinking: true
    }, function () {
      _this2.blinkTimeout = window.setTimeout(_this2.cancelBlink, 150);
    });
  }
  /**
  * @public
  * @param {number} start
  * @param {number} end
  */
  ;

  _proto.setSelectionRange = function setSelectionRange(start, end) {
    if (!this.input) {
      throw new Error('Cannot call "setSelectionRange" on unmounted Input');
    }

    if (document.activeElement !== this.input) {
      this.focus();
    }

    this.input.setSelectionRange(start, end);
  };

  _proto.render = function render() {
    var _this3 = this;

    return /*#__PURE__*/React.createElement(ThemeContext.Consumer, null, function (theme) {
      _this3.theme = theme;
      return /*#__PURE__*/React.createElement(CommonWrapper, _this3.props, _this3.renderMain);
    });
  }
  /**
  * @public
  */
  ;

  _proto.renderMaskedInput = function renderMaskedInput(inputProps, mask) {
    return /*#__PURE__*/React.createElement(MaskedInput, _extends({}, inputProps, {
      mask: mask,
      maskChar: this.props.maskChar,
      alwaysShowMask: this.props.alwaysShowMask,
      formatChars: this.props.formatChars,
      onChange: this.props.onChange,
      onValueChange: this.handleMaskedValueChange,
      onUnexpectedInput: this.handleUnexpectedInput
    }));
  };

  _proto.getIconSizeClassname = function getIconSizeClassname(right) {
    if (right === void 0) {
      right = false;
    }

    switch (this.props.size) {
      case 'large':
        return right ? styles.rightIconLarge(this.theme) : styles.leftIconLarge(this.theme);

      case 'medium':
        return right ? styles.rightIconMedium(this.theme) : styles.leftIconMedium(this.theme);

      case 'small':
      default:
        return right ? styles.rightIconSmall(this.theme) : styles.leftIconSmall(this.theme);
    }
  };

  _proto.renderLeftIcon = function renderLeftIcon() {
    return this.renderIcon(this.props.leftIcon, this.getIconSizeClassname());
  };

  _proto.renderRightIcon = function renderRightIcon() {
    return this.renderIcon(this.props.rightIcon, this.getIconSizeClassname(true));
  };

  _proto.renderIcon = function renderIcon(icon, sizeClassName) {
    var _cx5;

    if (!icon) {
      return null;
    }

    var disabled = this.props.disabled;
    var iconNode = icon instanceof Function ? icon() : icon;
    return /*#__PURE__*/React.createElement("span", {
      className: cx(styles.icon(), sizeClassName, styles.useDefaultColor(this.theme), (_cx5 = {}, _cx5[styles.iconDisabled()] = disabled, _cx5))
    }, iconNode);
  };

  _proto.renderPlaceholder = function renderPlaceholder() {
    var disabled = this.props.disabled;
    var focused = this.state.focused;
    var placeholder = null;

    if (this.state.polyfillPlaceholder && this.props.placeholder && !this.isMaskVisible && !this.props.value) {
      var _cx6;

      placeholder = /*#__PURE__*/React.createElement("div", {
        className: cx(styles.placeholder(this.theme), (_cx6 = {}, _cx6[styles.placeholderDisabled(this.theme)] = disabled, _cx6[styles.placeholderFocus(this.theme)] = focused, _cx6)),
        style: {
          textAlign: this.props.align || 'inherit'
        }
      }, this.props.placeholder);
    }

    return placeholder;
  };

  _proto.getSizeClassName = function getSizeClassName() {
    var _cx7, _cx8, _cx9;

    switch (this.props.size) {
      case 'large':
        return cx((_cx7 = {}, _cx7[styles.sizeLarge(this.theme)] = true, _cx7[styles.sizeLargeFallback(this.theme)] = isIE11 || isEdge, _cx7));

      case 'medium':
        return cx((_cx8 = {}, _cx8[styles.sizeMedium(this.theme)] = true, _cx8[styles.sizeMediumFallback(this.theme)] = isIE11 || isEdge, _cx8));

      case 'small':
      default:
        return cx((_cx9 = {}, _cx9[styles.sizeSmall(this.theme)] = true, _cx9[styles.sizeSmallFallback(this.theme)] = isIE11 || isEdge, _cx9));
    }
  };

  _createClass(Input, [{
    key: "isMaskVisible",
    get: function get() {
      var _this$props3 = this.props,
          mask = _this$props3.mask,
          alwaysShowMask = _this$props3.alwaysShowMask;
      var focused = this.state.focused;
      return Boolean(mask && (focused || alwaysShowMask));
    }
  }]);

  return Input;
}(React.Component);
Input.__KONTUR_REACT_UI__ = 'Input';
Input.defaultProps = {
  size: 'small'
};